import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
// ─── Palette identique au Centre d'Ajustement (version laboratoire) ─────────
const T = {
  bg:         "#020617",
  surface:    "#0a1628",
  surfaceAlt: "#0f1e35",
  surfaceHov: "#162540",
  text:       "#f8fafc",
  textMuted:  "#94a3b8",
  textFaint:  "#a6b5c9",
  textDim:    "rgba(250, 250, 250, 0.93)", 
  border:     "rgba(255,255,255,0.06)",
  borderMed:  "rgba(255,255,255,0.12)",
  orange:     "#fdba74",
  orangeDim:  "rgba(253,186,116,0.08)",
  sky:        "#7dd3fc",
  skyDim:     "rgba(125,211,252,0.08)",
  amber:      "#fcd34d",
  amberDim:   "rgba(252,211,77,0.08)",
  emerald:    "#6ee7b7",
  danger:     "#fca5a5",
  dangerDim:  "rgba(252,165,165,0.08)",
  r:          "12px",
  rSm:        "8px",
  rPill:      "20px",
  rXs:        "4px",
};

const TL = {
  bg:         "#f8fafc",
  surface:    "#ffffff",
  surfaceAlt: "#f1f5f9",
  surfaceHov: "#e2e8f0",
  text:       "#334155",
  textMuted:  "#64748b",
  textFaint:  "#94a3b8",
  border:     "rgba(0,0,0,0.05)",
  borderMed:  "rgba(0,0,0,0.10)",
  orange:     "#c2410c",
  orangeDim:  "rgba(194,65,12,0.06)",
  sky:        "#0369a1",
  skyDim:     "rgba(3,105,161,0.06)",
  amber:      "#b45309",
  amberDim:   "rgba(180,83,9,0.06)",
  emerald:    "#047857",
  danger:     "#b91c1c",
  dangerDim:  "rgba(185,28,28,0.06)",
  r:          "12px",
  rSm:        "8px",
  rPill:      "20px",
  rXs:        "4px",
};

const Mono = ({ children, size = 11, color = T.textMute, w = false, style = {} }) => (
  <span
    style={{
      fontFamily: "'DM Mono', 'Roboto Mono', monospace",
      fontSize: size,
      letterSpacing: '0.06em',
      color,
      fontWeight: w ? 700 : 400,
      lineHeight: 1,
      ...style,
    }}
  >
    {children}
  </span>
);

const STEPS = [
  { icon: "△", color: "danger",  label: "Diagnostiquer",       desc: "Identifiez les tensions et signaux dans la situation. Ces choix révèlent votre point de départ.", tag: "ÉTAPE 1" },
  { icon: "▣", color: "sky",     label: "Activer des leviers",  desc: "Sélectionnez les actions pertinentes à chaque étape. Chaque décision modifie les indicateurs en temps réel.", tag: "ÉTAPE 2" },
  { icon: "◉", color: "amber",   label: "Choisir une posture",  desc: "À la fin du parcours, définissez votre configuration finale. Ce choix détermine votre profil.", tag: "ÉTAPE 3" },
  { icon: "→", color: "emerald", label: "Lire le débrief",      desc: "Accédez à votre rapport complet : indicateurs, analyse décisionnelle, pistes de transfert.", tag: "RAPPORT" },
];

const UI_TIPS = [
  { icon: "⬡", text: "Les indicateurs dans la colonne gauche évoluent à chaque action — observez-les attentivement." },
  { icon: "◈", text: "Le panneau droit affiche le feedback détaillé après chaque choix effectué." },
  { icon: "◎", text: "Le bouton « Retour » annule la dernière action si vous souhaitez explorer une autre voie." },
  { icon: "◇", text: "Changez de template via le menu en haut à droite pour explorer d'autres cas d'étude." },
];

const M = ({ children, size = 11, color = T.textFaint, weight = false, style = {} }) => (
  <span style={{
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: size,
    letterSpacing: '0.03em',
    color,
    fontWeight: weight ? 700 : 400,
    lineHeight: 1,
    ...style
  }}>
    {children}
  </span>
);

function MockInterface({ th }) {
  const metrics = [
    { label: "Confiance",  val: 62, color: th.amber   },
    { label: "Clarté",     val: 44, color: th.sky     },
    { label: "Cohésion",   val: 78, color: th.emerald },
    { label: "Engagement", val: 35, color: th.orange  },
  ];
  const actions = [
    { label: "Identifier les besoins réels de l'enfant", active: true },
    { label: "Proposer un cadre de rencontre tripartite", active: false },
    { label: "Documenter les observations terrain",       active: false },
  ];
  return (
    <div style={{ display: "flex", height: "100%", gap: 0, overflow: "hidden" }}>
      <div style={{
        width: "26%",
        borderRight: `1px solid ${th.border}`,
        background: th.surfaceAlt,
        padding: "12px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        flexShrink: 0
      }}>
        <M size={8} color={th.textFaint} weight>⟡ INDICATEURS</M>
        {metrics.map(m => (
          <div key={m.label}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 8, color: th.textMuted }}>{m.label}</span>
              <M size={9} color={m.color} weight>{m.val}</M>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ width: `${m.val}%`, height: "100%", background: m.color, borderRadius: 2, boxShadow: `0 0 4px ${m.color}70` }} />
            </div>
          </div>
        ))}
        <div style={{ marginTop: 6 }}>
          <M size={8} color={th.textFaint} weight>⟡ ACTEURS</M>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
            {["Enfant", "Parents", "École", "Équipe"].map((f, i) => (
              <div key={f} style={{
                padding: "2px 6px",
                borderRadius: 4,
                fontSize: 7,
                background: i < 2 ? `${th.sky}14` : "rgba(255,255,255,0.04)",
                border: `1px solid ${i < 2 ? `${th.sky}35` : th.border}`,
                color: i < 2 ? th.sky : th.textFaint,
                fontFamily: "'JetBrains Mono', monospace"
              }}>{f}</div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, padding: "12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{
          padding: "8px 10px",
          background: th.surfaceAlt,
          borderRadius: 6,
          border: `1px solid ${th.border}`
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
            <M size={8} color={th.orange} weight style={{ background: th.orangeDim, padding: "1px 7px", borderRadius: 12, border: `1px solid ${th.orange}28` }}>
              Activation · 2/4
            </M>
          </div>
          <p style={{ fontSize: 10, fontWeight: 700, color: th.text, margin: "0 0 3px", lineHeight: 1.3 }}>
            Réunion de coordination
          </p>
          <p style={{ fontSize: 8, color: th.textMuted, margin: 0, fontStyle: "italic", lineHeight: 1.55 }}>
            Comment organiser la concertation entre les acteurs ?
          </p>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
          {actions.map((a, i) => (
            <div key={i} style={{
              padding: "8px 10px",
              borderRadius: 6,
              background: a.active ? `${th.amber}08` : th.surfaceAlt,
              border: `1.5px solid ${a.active ? `${th.amber}35` : th.border}`,
              borderLeft: `3px solid ${a.active ? th.amber : th.border}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 6
            }}>
              <M size={8} color={a.active ? th.amber : th.textFaint} weight style={{ flexShrink: 0, marginTop: 1 }}>
                {a.active ? "✓" : `0${i + 1}`}
              </M>
              <span style={{ fontSize: 8, color: a.active ? th.textMuted : th.text, lineHeight: 1.45 }}>{a.label}</span>
            </div>
          ))}
        </div>
        <div style={{
          padding: "7px 12px",
          borderRadius: 6,
          background: th.orange,
          textAlign: "center",
          cursor: "default"
        }}>
          <M size={9} color="#1a0a00" weight>⟡ CONTINUER →</M>
        </div>
      </div>

      <div style={{
        width: "28%",
        borderLeft: `1px solid ${th.border}`,
        background: th.surfaceAlt,
        padding: "12px 10px",
        borderTop: `2.5px solid ${th.sky}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        flexShrink: 0
      }}>
        <M size={8} color={th.textFaint} weight>⟡ ANALYSE D'IMPACT</M>
        <div style={{
          padding: "7px 9px",
          background: `${th.sky}09`,
          borderLeft: `2px solid ${th.sky}`,
          borderRadius: 4
        }}>
          <p style={{ fontSize: 8, color: th.text, lineHeight: 1.6, margin: 0 }}>
            Identifier les besoins réels permet de sortir d'une logique de rapport pour entrer dans une co-construction.
          </p>
        </div>
        <div style={{
          padding: "7px 9px",
          background: th.dangerDim,
          border: `1px solid ${th.danger}20`,
          borderRadius: 4
        }}>
          <p style={{ fontSize: 8, fontWeight: 600, color: th.danger, margin: "0 0 2px" }}>Ce que ça coûte</p>
          <p style={{ fontSize: 8, color: th.textMuted, margin: 0, lineHeight: 1.5 }}>Sans dialogue, les tensions restent invisibles.</p>
        </div>
        <button style={{
          fontSize: 8,
          color: th.sky,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          textAlign: "left",
          textDecoration: "underline",
          marginTop: "auto",
          fontFamily: "'JetBrains Mono', monospace"
        }}>
          Comprendre le fonctionnement →
        </button>
      </div>
    </div>
  );
}

function MockMetrics({ th }) {
  const data = [
    { label: "Confiance institutionnelle", val: 62, prev: 45, color: th.amber,   icon: "◎" },
    { label: "Clarté du mandat",           val: 44, prev: 50, color: th.sky,     icon: "⬡" },
    { label: "Cohésion d'équipe",          val: 78, prev: 60, color: th.emerald, icon: "◈" },
    { label: "Engagement famille",         val: 35, prev: 35, color: th.orange,  icon: "△" },
  ];
  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <M size={10} color={th.text} weight>Évolution des indicateurs</M>
        <M size={8} color={th.textFaint} style={{ marginLeft: "auto", background: th.surfaceAlt, padding: "2px 8px", borderRadius: 20, border: `1px solid ${th.border}` }}>
          après 6 actions
        </M>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flex: 1 }}>
        {data.map(m => {
          const delta = m.val - m.prev;
          return (
            <div key={m.label} style={{
              padding: "12px 13px",
              borderRadius: 8,
              background: th.surface,
              border: `1px solid ${th.border}`
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 7 }}>
                <span style={{ fontSize: 11, color: th.textFaint }}>{m.icon}</span>
                <span style={{ fontSize: 8, color: th.textMuted, lineHeight: 1.3 }}>{m.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginBottom: 7 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: th.text, fontFamily: "'Inter', sans-serif" }}>{m.val}</span>
                {delta !== 0 && (
                  <M size={10} color={delta > 0 ? th.emerald : th.danger} weight>
                    {delta > 0 ? "+" : ""}{delta}
                  </M>
                )}
              </div>
              <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
                <div style={{ width: `${m.val}%`, height: "100%", background: m.color, borderRadius: 2, boxShadow: `0 0 5px ${m.color}70`, transition: "width 1s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <M size={7} color={th.textFaint}>départ : {m.prev}</M>
                <M size={7} color={th.textFaint}>/ 100</M>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MockDebrief({ th }) {
  const [tab, setTab] = useState(0);
  const tabs = ["Vue d'ensemble", "Indicateurs", "Décisions", "Étapes"];
  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{
        width: "38%",
        background: th.surfaceAlt,
        borderRight: `1px solid ${th.border}`,
        padding: "12px 8px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        flexShrink: 0
      }}>
        <M size={8} color={th.textFaint} weight style={{ margin: "0 4px 8px", letterSpacing: "0.05em" }}>
          ⟡ RAPPORT FINAL
        </M>
        <div style={{
          padding: "6px 8px",
          marginBottom: 6,
          borderRadius: 6,
          background: `${th.amber}08`,
          border: `1px solid ${th.amber}20`
        }}>
          <M size={8} color={th.amber} weight style={{ margin: "0 0 2px" }}>Configuration_Pro</M>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <M size={8} color={th.textFaint}>Score global</M>
            <M size={10} color={th.amber} weight>72%</M>
          </div>
        </div>
        {tabs.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            textAlign: "left",
            padding: "6px 8px",
            borderRadius: 6,
            border: "none",
            width: "100%",
            background: i === tab ? `${th.amber}10` : "none",
            borderLeft: `2px solid ${i === tab ? th.amber : "transparent"}`,
            color: i === tab ? th.amber : th.textMuted,
            fontSize: 9,
            cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace",
            transition: "all 0.15s"
          }}>
            {t}
          </button>
        ))}
        <div style={{
          marginTop: "auto",
          borderTop: `1px solid ${th.border}`,
          paddingTop: 8,
          display: "flex",
          gap: 4,
          justifyContent: "center"
        }}>
          {tabs.map((_, i) => (
            <div key={i} onClick={() => setTab(i)} style={{
              width: i === tab ? 16 : 5,
              height: 5,
              borderRadius: 3,
              cursor: "pointer",
              background: i === tab ? th.amber : `${th.textFaint}40`,
              transition: "all 0.2s"
            }} />
          ))}
        </div>
      </div>

      <div style={{ flex: 1, padding: "14px 14px", display: "flex", flexDirection: "column", gap: 9, overflow: "hidden" }}>
        {tab === 0 && <>
          <div style={{ borderLeft: `3px solid ${th.amber}`, paddingLeft: 10 }}>
            <M size={8} color={th.amber} weight style={{ margin: "0 0 3px" }}>Configuration_Pro · 72%</M>
            <p style={{ fontSize: 11, fontWeight: 700, color: th.text, margin: "0 0 4px", lineHeight: 1.3 }}>
              Vous avez su activer les bons leviers relationnels dans un contexte tendu
            </p>
            <p style={{ fontSize: 8, color: th.textMuted, margin: 0, fontStyle: "italic", lineHeight: 1.55 }}>
              Votre posture d'accompagnement s'appuie sur une lecture fine des dynamiques familiales.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
            {[{ l: "Potentiel", v: "85%", c: th.text }, { l: "Écart réel", v: "−13%", c: th.danger }, { l: "Score", v: "72%", c: th.amber }].map(x => (
              <div key={x.l} style={{ padding: "8px 10px", background: th.surface, border: `1px solid ${th.border}`, borderRadius: 7 }}>
                <M size={7} color={th.textFaint} style={{ margin: "0 0 3px" }}>{x.l}</M>
                <p style={{ fontSize: 15, fontWeight: 800, color: x.c, margin: 0 }}>{x.v}</p>
              </div>
            ))}
          </div>
          <div style={{ padding: "8px 10px", background: th.amberDim, border: `1px solid ${th.amber}28`, borderRadius: 7 }}>
            <M size={8} color={th.amber} weight style={{ margin: "0 0 3px" }}>⟡ Insight clé</M>
            <p style={{ fontSize: 8, color: th.text, fontStyle: "italic", margin: 0, lineHeight: 1.55 }}>
              "La qualité de la relation prime sur la technicité du protocole."
            </p>
          </div>
        </>}
        {tab !== 0 && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <M size={8} color={th.textFaint} weight>Section : {tabs[tab]}</M>
            <div style={{ padding: "12px", background: th.surface, borderRadius: 8, border: `1px solid ${th.border}` }}>
              <p style={{ fontSize: 9, color: th.textMuted, lineHeight: 1.65, margin: 0 }}>
                Analyse complète générée à partir de vos choix tout au long du parcours.
              </p>
            </div>
          </div>
        )}
        <div style={{ marginTop: "auto", display: "flex", justifyContent: "space-between", borderTop: `1px solid ${th.border}`, paddingTop: 8 }}>
          <button onClick={() => setTab(t => Math.max(0, t - 1))} style={{
            fontSize: 8,
            color: tab > 0 ? th.textMuted : "transparent",
            background: "none",
            border: "none",
            cursor: tab > 0 ? "pointer" : "default",
            fontFamily: "'JetBrains Mono', monospace"
          }}>← Précédent</button>
          <M size={8} color={th.textFaint}>{tab + 1} / {tabs.length}</M>
          <button onClick={() => setTab(t => Math.min(tabs.length - 1, t + 1))} style={{
            fontSize: 8,
            color: tab < tabs.length - 1 ? th.textMuted : "transparent",
            background: "none",
            border: "none",
            cursor: tab < tabs.length - 1 ? "pointer" : "default",
            fontFamily: "'JetBrains Mono', monospace"
          }}>Suivant →</button>
        </div>
      </div>
    </div>
  );
}

const SLIDES = [
  { id: "interface", label: "Interface 3 colonnes", caption: "Indicateurs · Actions · Feedback en temps réel", Component: MockInterface },
  { id: "metrics",   label: "Indicateurs dynamiques", caption: "Chaque décision impacte visiblement les métriques", Component: MockMetrics },
  { id: "debrief",   label: "Rapport final", caption: "Analyse complète avec navigation par sections", Component: MockDebrief },
];

function PreviewCarousel({ th }) {
  const [idx, setIdx] = useState(0);
  const [anim, setAnim] = useState(false);
  const [dir, setDir] = useState(1);
  const timerRef = useRef(null);
  const dragStartX = useRef(null);

  const go = useCallback((next) => {
    const target = ((next % SLIDES.length) + SLIDES.length) % SLIDES.length;
    setDir(next > idx ? 1 : -1);
    setAnim(true);
    setTimeout(() => { setIdx(target); setAnim(false); }, 280);
  }, [idx]);

  useEffect(() => {
    timerRef.current = setInterval(() => go(idx + 1), 6000);
    return () => clearInterval(timerRef.current);
  }, [idx, go]);

  const stopAuto = () => clearInterval(timerRef.current);

  const onPointerDown = e => { dragStartX.current = e.clientX; };
  const onPointerUp = e => {
    if (dragStartX.current === null) return;
    const dx = e.clientX - dragStartX.current;
    dragStartX.current = null;
    if (Math.abs(dx) > 36) { stopAuto(); go(idx + (dx < 0 ? 1 : -1)); }
  };

  const { Component } = SLIDES[idx];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", gap: 4 }}>
        {SLIDES.map((s, i) => (
          <button key={s.id} onClick={() => { stopAuto(); go(i); }} style={{
            flex: 1,
            padding: "5px 0",
            borderRadius: T.rSm,
            background: i === idx ? `${th.orange}12` : "none",
            border: `1px solid ${i === idx ? `${th.orange}35` : th.border}`,
            color: i === idx ? th.orange : th.textFaint,
            fontSize: 9,
            cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace",
            transition: "all 0.2s",
            letterSpacing: "0.03em"
          }}>
            {s.label}
          </button>
        ))}
      </div>

      <div
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        style={{
          flex: 1,
          borderRadius: T.r,
          overflow: "hidden",
          background: th.surfaceAlt,
          border: `1px solid ${th.borderMed}`,
          boxShadow: `0 16px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)`,
          position: "relative",
          cursor: "grab",
          userSelect: "none",
          minHeight: "clamp(280px, 38vh, 400px)",
        }}>
        <div style={{
          height: 28,
          background: th.surface,
          borderBottom: `1px solid ${th.border}`,
          display: "flex",
          alignItems: "center",
          paddingLeft: 12,
          gap: 8,
          flexShrink: 0
        }}>
          <div style={{ width: 8, height: 8, border: `1px solid ${th.orange}`, opacity: 0.4 }} />
          <M size={9} color={th.text} weight style={{ textTransform: "uppercase", letterSpacing: "0.1em" }}>
            {SLIDES[idx].label}
          </M>
        </div>

        <div style={{
          height: "calc(100% - 28px)",
          overflow: "hidden",
          opacity: anim ? 0 : 1,
          transform: anim ? `translateX(${dir * 16}px)` : "translateX(0)",
          transition: "opacity 0.28s ease, transform 0.28s ease"
        }}>
          <Component th={th} />
        </div>

        <div style={{
          position: "absolute",
          inset: "28px 0 0 0",
          pointerEvents: "none",
          background: `linear-gradient(90deg, ${th.surfaceAlt}30 0%, transparent 6%, transparent 94%, ${th.surfaceAlt}30 100%)`
        }} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: th.text, margin: "0 0 1px", fontFamily: "'Inter', sans-serif" }}>
            {SLIDES[idx].label}
          </p>
          <p style={{ fontSize: 11, color: th.textFaint, margin: 0 }}>{SLIDES[idx].caption}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button onClick={() => { stopAuto(); go(idx - 1); }} style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            border: `1px solid ${th.border}`,
            background: th.surfaceAlt,
            color: th.textMuted,
            fontSize: 14,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s"
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = th.orange; e.currentTarget.style.color = th.orange; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = th.border; e.currentTarget.style.color = th.textMuted; }}>‹</button>
          <div style={{ display: "flex", gap: 5 }}>
            {SLIDES.map((s, i) => (
              <button key={s.id} onClick={() => { stopAuto(); go(i); }} style={{
                width: i === idx ? 18 : 6,
                height: 6,
                borderRadius: 3,
                background: i === idx ? th.orange : `${th.textFaint}45`,
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.25s"
              }} />
            ))}
          </div>
          <button onClick={() => { stopAuto(); go(idx + 1); }} style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            border: `1px solid ${th.border}`,
            background: th.surfaceAlt,
            color: th.textMuted,
            fontSize: 14,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.15s"
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = th.orange; e.currentTarget.style.color = th.orange; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = th.border; e.currentTarget.style.color = th.textMuted; }}>›</button>
        </div>
      </div>
    </div>
  );
}

function Dot({ color, th, pulse }) {
  const c = th[color] || th.orange;
  return (
    <span style={{
      display: "inline-block",
      width: 7,
      height: 7,
      borderRadius: "50%",
      background: c,
      boxShadow: `0 0 8px ${c}80`,
      animation: pulse ? "lab-pulse 2.5s ease-in-out infinite" : "none",
      flexShrink: 0
    }} />
  );
}

function StepCard({ step, index, th, visible }) {
  const c = th[step.color] || th.orange;
  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(14px)",
      transition: `opacity 0.45s ease ${index * 0.08}s, transform 0.45s ease ${index * 0.08}s`,
      padding: "18px 20px",
      borderRadius: T.r,
      background: th.surface,
      border: `1px solid ${th.border}`,
      borderTop: `2.5px solid ${c}`,
      display: "flex",
      flexDirection: "column",
      gap: "10px"
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{
          fontSize: "20px",
          color: c,
          width: 36,
          height: 36,
          borderRadius: T.rSm,
          background: `${c}12`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${c}22`
        }}>{step.icon}</span>
        <M size={10} color={th.textFaint} weight style={{ background: th.surfaceAlt, padding: "2px 9px", borderRadius: 20, border: `1px solid ${th.border}` }}>
          {step.tag}
        </M>
      </div>
      <div>
        <p style={{ fontSize: "14px", fontWeight: 600, color: th.text, margin: "0 0 6px", lineHeight: 1.3, fontFamily: "'Inter', sans-serif" }}>
          {step.label}
        </p>
        <p style={{ fontSize: "12px", color: th.textMuted, lineHeight: "1.7", margin: 0 }}>{step.desc}</p>
      </div>
    </div>
  );
}

function TipRow({ tip, th, visible, delay }) {
  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0)" : "translateX(-8px)",
      transition: `opacity 0.4s ease ${delay}s, transform 0.4s ease ${delay}s`,
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      padding: "11px 14px",
      borderRadius: T.rSm,
      background: th.surfaceAlt,
      border: `1px solid ${th.border}`
    }}>
      <span style={{ fontSize: "14px", color: th.textFaint, flexShrink: 0, marginTop: "1px" }}>{tip.icon}</span>
      <p style={{ fontSize: "12px", color: th.textMuted, lineHeight: "1.65", margin: 0 }}>{tip.text}</p>
    </div>
  );
}

function Background({ isDark }) {
  if (!isDark) return null;
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      <div style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `linear-gradient(rgba(125,211,252,0.03) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(125,211,252,0.03) 1px, transparent 1px)`,
        backgroundSize: "48px 48px"
      }} />
      <div style={{
        position: "absolute",
        top: "-15%",
        left: "-10%",
        width: "55vw",
        height: "55vw",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(253,186,116,0.06) 0%, transparent 65%)",
        animation: "lab-float 12s ease-in-out infinite"
      }} />
      <div style={{
        position: "absolute",
        bottom: "-20%",
        right: "-5%",
        width: "50vw",
        height: "50vw",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(125,211,252,0.05) 0%, transparent 65%)",
        animation: "lab-float 15s ease-in-out infinite reverse"
      }} />
    </div>
  );
}

export default function CentreAjustementIntro({
  caseData = null,
  onStart = () => {},
  themeMode = "dark",
}) {
  const [isDark, setDark] = useState(themeMode === "dark");
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState("idle");
  const navigate = useNavigate();

  const th = isDark ? T : TL;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleStart = () => {
    setPhase("loading");
    setTimeout(() => {
      setPhase("ready");
      onStart();
      window.location.href = "/centre-ajustement";
    }, 700);
  };

  const handleThemeToggle = () => setDark(prev => !prev);

  const title = caseData?.title || "Situations Réelles PESACORE";
  const desc = caseData?.description || "Des études de cas pour développer votre posture personnelle et/ou professionnelle, en situations complexes. Chaque décision modifie les indicateurs en temps réel.";
  const tags = caseData?.tags || [];
  const time = caseData?.estimatedTime;
  const diff = caseData?.difficulty;

  return (
    <div style={{
      minHeight: "100vh",
      background: th.bg,
      color: th.text,
      fontFamily: "'Inter', 'JetBrains Mono', monospace",
      position: "relative",
      overflowX: "hidden"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        
        @keyframes lab-pulse {
          0%,100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        
        @keyframes lab-float {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-28px); }
        }
        
        @keyframes lab-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .lab-btn:hover { opacity: 0.88 !important; transform: translateY(-1px) !important; }
        .lab-btn:active { transform: translateY(0px) !important; }
        .lab-btn-sec:hover { background: ${th.surfaceHov} !important; }
        
        .lab-hero { display: flex; gap: 48px; align-items: flex-start; }
        .lab-hero-left { flex: 0 0 380px; display: flex; flex-direction: column; gap: 24px; }
        .lab-hero-right { flex: 1; min-width: 0; display: flex; flex-direction: column; }
        
        @media (max-width: 860px) {
          .lab-hero { flex-direction: column; }
          .lab-hero-right { display: none; }
          .lab-grid { grid-template-columns: 1fr 1fr !important; }
          .lab-layout { flex-direction: column !important; }
          .lab-sidebar { width: 100% !important; }
        }
        
        @media (max-width: 500px) {
          .lab-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <Background isDark={isDark} />

      <header style={{
        padding: "0 16px",
        height: "56px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${th.border}`,
        background: isDark ? "rgba(10, 10, 15, 0.95)" : "rgba(248, 249, 252, 0.95)",
        backdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 100,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {/* Gauche : retour + pulse (masqué sur mobile si besoin) */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          {/* Bouton retour */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: T.textMute, padding: 0, transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.textDim}
            onMouseLeave={e => e.currentTarget.style.color = T.textMuted}
          >
            <ArrowLeft size={13} />
            <Mono size={10} color="inherit">Dashboard</Mono>
          </button>

          {/* Pulse visible même sur mobile */}
          <div style={{ position: "relative", width: 6, height: 6, flexShrink: 0 }}>
            <div style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: th.orange,
              animation: "lab-pulse 2.5s ease-in-out infinite"
            }} />
          </div>
        </div>

        {/* Centre : titre + badge (responsive) */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flex: 1,
          justifyContent: "center",
          margin: "0 8px",
        }}>
          <span style={{
            fontSize: "clamp(12px, 4vw, 14px)",
            fontWeight: 700,
            color: th.text,
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            Centre d'Ajustement
          </span>
          <span style={{
            fontSize: "8px",
            fontWeight: 700,
            color: th.textFaint,
            background: th.surfaceAlt,
            padding: "2px 6px",
            borderRadius: "20px",
            border: `1px solid ${th.border}`,
            letterSpacing: "0.09em",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}>
            PESACORE
          </span>
        </div>

        {/* Droite : bouton thème */}
        <button
          onClick={handleThemeToggle}
          style={{
            width: 32,
            height: 32,
            borderRadius: "6px",
            background: th.surfaceAlt,
            border: `1px solid ${th.border}`,
            color: th.textSub,
            fontSize: "13px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isDark ? "☀️" : "🌙"}
        </button>
      </header>

      <div style={{
        position: "relative",
        zIndex: 5,
        maxWidth: "1100px",
        margin: "0 auto",
        padding: "52px 24px 80px",
        display: "flex",
        flexDirection: "column",
        gap: "52px"
      }}>
        <section className="lab-hero" style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 0.55s ease, transform 0.55s ease"
        }}>
          <div className="lab-hero-left">
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Dot color="orange" th={th} />
                <M size={11} color={th.orange} weight style={{ letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  Étude interactive
                </M>
              </div>
              {tags.map(tag => (
                <M key={tag} size={11} color={th.textFaint} weight style={{ background: th.surfaceAlt, padding: "2px 9px", borderRadius: 20, border: `1px solid ${th.border}` }}>
                  {tag}
                </M>
              ))}
              {time && (
                <M size={11} color={th.textFaint} weight style={{ background: th.surfaceAlt, padding: "2px 9px", borderRadius: 20, border: `1px solid ${th.border}`, marginLeft: "auto" }}>
                  ⏱ ~{time} min
                </M>
              )}
              {diff && (
                <M size={11} color={th.amber} weight style={{ background: th.amberDim, padding: "2px 9px", borderRadius: 20, border: `1px solid ${th.amber}28` }}>
                  {diff}
                </M>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <h1 style={{
                fontSize: "clamp(28px, 3.5vw, 42px)",
                fontWeight: 800,
                color: th.text,
                lineHeight: 1.12,
                letterSpacing: "-0.025em",
                margin: 0,
                fontFamily: "'Inter', sans-serif"
              }}>{title}</h1>
              <p style={{
                fontSize: "15px",
                color: th.textMuted,
                lineHeight: "1.78",
                fontStyle: "italic",
                margin: 0
              }}>{desc}</p>
            </div>

            {caseData?.metrics && Object.keys(caseData.metrics).length > 0 && (
              <div>
                <M size={11} color={th.textFaint} weight style={{ marginBottom: "10px", letterSpacing: "0.03em" }}>
                  ⟡ INDICATEURS SUIVIS
                </M>
                <div style={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
                  {Object.entries(caseData.metrics).slice(0, 4).map(([k, v]) => (
                    <div key={k} style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "7px 12px",
                      borderRadius: T.rSm,
                      background: th.surface,
                      border: `1px solid ${th.border}`
                    }}>
                      <span style={{ fontSize: "13px", color: th.textFaint }}>{v.icon || "⬡"}</span>
                      <div>
                        <p style={{ fontSize: "11px", fontWeight: 600, color: th.text, margin: 0, fontFamily: "'Inter', sans-serif" }}>{v.label || k}</p>
                        <M size={9} color={th.textFaint}>départ : {caseData.initialState?.parameters?.[k] ?? 50}</M>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <button className="lab-btn" onClick={handleStart}
                disabled={phase === "loading" || phase === "ready"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "14px 26px",
                  borderRadius: T.r,
                  border: "none",
                  background: phase === "ready" ? th.amber : th.orange,
                  color: "#1a0a00",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: phase === "idle" ? "pointer" : "default",
                  transition: "all 0.18s",
                  boxShadow: `0 6px 24px ${th.orange}35`,
                  minWidth: "190px",
                  justifyContent: "center",
                  fontFamily: "'JetBrains Mono', monospace"
                }}>
                {phase === "loading" ? (
                  <><span style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "2px solid #1a0a0040",
                    borderTopColor: "#1a0a00",
                    animation: "lab-spin 0.7s linear infinite",
                    display: "inline-block"
                  }} />Chargement…</>
                ) : phase === "ready" ? <>✓ Démarré</> : <>⟡ LANCER L'ÉTUDE →</>}
              </button>
              <button className="lab-btn-sec"
                onClick={() => document.getElementById("guide")?.scrollIntoView({ behavior: "smooth" })}
                style={{
                  padding: "14px 22px",
                  borderRadius: T.r,
                  background: "none",
                  border: `1px solid ${th.borderMed}`,
                  color: th.textMuted,
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  fontFamily: "'JetBrains Mono', monospace"
                }}>
                [ voir le guide ]
              </button>
            </div>
          </div>

          <div className="lab-hero-right">
            <PreviewCarousel th={th} />
          </div>
        </section>

        <section id="guide" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div style={{
            display: "flex",
            alignItems: "baseline",
            gap: "12px",
            paddingBottom: "16px",
            borderBottom: `1px solid ${th.border}`
          }}>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: th.text, margin: 0, fontFamily: "'Inter', sans-serif" }}>Guide rapide</h2>
            <M size={12} color={th.textFaint}>4 étapes pour bien naviguer</M>
          </div>
          <div className="lab-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px" }}>
            {STEPS.map((step, i) => <StepCard key={i} step={step} index={i} th={th} visible={visible} />)}
          </div>
        </section>

        <section className="lab-layout" style={{ display: "flex", gap: "32px" }}>
          <div className="lab-sidebar" style={{ width: "260px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <M size={11} color={th.textFaint} weight style={{ letterSpacing: "0.03em", marginBottom: "6px" }}>
                ⟡ REPÈRES VISUELS
              </M>
              <p style={{ fontSize: "12px", color: th.textMuted, lineHeight: "1.72" }}>
                L'interface est divisée en trois colonnes. Voici comment les lire.
              </p>
            </div>
            <div style={{
              padding: "14px",
              borderRadius: T.r,
              background: th.surface,
              border: `1px solid ${th.border}`,
              display: "flex",
              gap: "5px",
              height: "110px",
              alignItems: "stretch"
            }}>
              {[
                { label: "Éco-\nsystème", accent: th.textFaint, flex: 1 },
                { label: "Actions", accent: th.orange, flex: 2 },
                { label: "Feed-\nback", accent: th.sky, flex: 1 },
              ].map((col, i) => (
                <div key={i} style={{
                  flex: col.flex,
                  borderRadius: T.rSm,
                  background: i === 1 ? `${th.orange}09` : th.surfaceAlt,
                  border: `1px solid ${i === 1 ? `${th.orange}22` : th.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "6px"
                }}>
                  <M size={9} color={col.accent} weight style={{ textAlign: "center", lineHeight: 1.5, whiteSpace: "pre-line" }}>
                    {col.label}
                  </M>
                </div>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <M size={11} color={th.textFaint} weight style={{ letterSpacing: "0.03em", marginBottom: "6px" }}>
              ⟡ ASTUCES D'UTILISATION
            </M>
            {UI_TIPS.map((tip, i) => (
              <TipRow key={i} tip={tip} th={th} visible={visible} delay={0.25 + i * 0.07} />
            ))}
          </div>
        </section>

        <section style={{
          opacity: visible ? 1 : 0,
          transition: "opacity 0.55s ease 0.4s",
          padding: "28px 32px",
          borderRadius: T.r,
          background: isDark
            ? "linear-gradient(135deg, rgba(253,186,116,0.07) 0%, rgba(125,211,252,0.05) 100%)"
            : "linear-gradient(135deg, rgba(194,65,12,0.04) 0%, rgba(3,105,161,0.04) 100%)",
          border: `1px solid ${th.borderMed}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "20px",
          flexWrap: "wrap"
        }}>
          <div>
            <p style={{ fontSize: "16px", fontWeight: 700, color: th.text, margin: "0 0 6px", fontFamily: "'Inter', sans-serif" }}>
              Prêt à commencer ?
            </p>
            <p style={{ fontSize: "13px", color: th.textMuted, margin: 0, lineHeight: "1.65" }}>
              Vos choix sont enregistrés en temps réel. Vous pourrez revenir en arrière à tout moment.
            </p>
          </div>
          <button className="lab-btn" onClick={handleStart} disabled={phase !== "idle"}
            style={{
              padding: "13px 24px",
              borderRadius: T.r,
              border: "none",
              background: phase === "ready" ? th.amber : th.orange,
              color: "#1a0a00",
              fontSize: "13px",
              fontWeight: 700,
              cursor: phase === "idle" ? "pointer" : "default",
              transition: "all 0.18s",
              boxShadow: `0 4px 16px ${th.orange}30`,
              whiteSpace: "nowrap",
              minWidth: "180px",
              display: "flex",
              alignItems: "center",
              gap: "7px",
              justifyContent: "center",
              fontFamily: "'JetBrains Mono', monospace"
            }}>
            {phase === "loading" ? (
              <><span style={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                border: "2px solid #1a0a0040",
                borderTopColor: "#1a0a00",
                animation: "lab-spin 0.7s linear infinite",
                display: "inline-block"
              }} />Chargement…</>
            ) : phase === "ready" ? "✓ Démarré" : "⟡ LANCER L'ENTRAÎNEMENT →"}
          </button>
        </section>
      </div>
    </div>
  );
}